import { AuthError } from './errors.ts'
import {
  changePassword as changePasswordCall,
  confirmForgotPassword,
  forgotPassword,
  globalSignOut,
  initiateUserPasswordAuth,
  respondToNewPasswordRequired,
  respondToSoftwareTokenMfa,
  toTokenResponse,
  type AuthResponse,
} from './idp-operations.ts'
import type { AuthKernel } from './kernel.ts'
import { adminScopedAccessToken } from './scope.ts'
import type { Leaving, PasswordResetDelivery, SignInChallenge, SignInOutcome } from './types.ts'

// The password half of the client: email + password against the IDP API, for the back office. Why
// admin-panel deviates from code + PKCE is in the README and docs/design/frontend-v1.md §5.

/** Mirrors `auth_session_validity` on the app client (infra/modules/cognito). */
const CHALLENGE_LIFETIME_MS = 5 * 60_000

export interface PasswordApi {
  signIn(credentials: { readonly email: string; readonly password: string }): Promise<SignInOutcome>
  /** Completes a first access. On success a session exists, as after `signIn`. */
  completeNewPassword(challenge: SignInChallenge, newPassword: string): Promise<void>
  /** Answers the second factor. On success a session exists, as after `signIn`. */
  completeMfa(challenge: SignInChallenge, code: string): Promise<void>
  changePassword(current: string, next: string): Promise<void>
  requestReset(email: string): Promise<PasswordResetDelivery>
  /** Does not authenticate: the caller chains `signIn`, so one path establishes a session. */
  confirmReset(input: {
    readonly email: string
    readonly code: string
    readonly newPassword: string
  }): Promise<void>
  /** Ends the account in use on every device, and takes it off this one. Failure is not fatal: the
   *  local account still goes. One device only is `accounts.signOut`. */
  signOut(options?: SignOutOptions): Promise<void>
  /** `signOut`, settled as soon as the account is off this device: the revocation goes on behind. */
  leave(options?: SignOutOptions): Promise<Leaving>
}

/** `navigate: false` when the app takes the user to its own door. */
export interface SignOutOptions {
  readonly navigate?: boolean
}

function requiresNothingElse(attributes: string | undefined): boolean {
  if (attributes === undefined) return true
  try {
    return (JSON.parse(attributes) as readonly string[]).length === 0
  } catch {
    return false
  }
}

function pendingChallenge(response: AuthResponse): SignInChallenge {
  const username = response.ChallengeParameters?.USER_ID_FOR_SRP
  if (response.Session === undefined || username === undefined) {
    throw new AuthError('idp_request_failed', 'The challenge carried no Session or username')
  }
  return { session: response.Session, username, expiresAt: Date.now() + CHALLENGE_LIFETIME_MS }
}

/** The outcome for a challenge, or an `AuthError` when it is one this app cannot answer. */
function outcomeFrom(response: AuthResponse): SignInOutcome {
  if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
    return { kind: 'mfa-required', challenge: pendingChallenge(response) }
  }
  if (response.ChallengeName !== 'NEW_PASSWORD_REQUIRED') {
    // SMS and MFA_SETUP among them: neither is reachable on this pool, and inventing an
    // answer we cannot give would be worse than saying so.
    throw new AuthError(
      'challenge_unsupported',
      `Cognito asked for "${response.ChallengeName ?? 'nothing'}", which this app cannot answer`,
    )
  }
  if (!requiresNothingElse(response.ChallengeParameters?.requiredAttributes)) {
    throw new AuthError(
      'challenge_unsupported',
      'The challenge requires user attributes this app cannot invent',
    )
  }
  return { kind: 'new-password-required', challenge: pendingChallenge(response) }
}

export function createPasswordApi(
  kernel: AuthKernel,
  navigate: (url: string) => void,
): PasswordApi {
  const { clientId } = kernel.config

  async function establish(response: AuthResponse): Promise<void> {
    if (response.AuthenticationResult === undefined) {
      throw new AuthError('idp_request_failed', 'The IDP answered without tokens')
    }
    await kernel.applyTokens(toTokenResponse(response.AuthenticationResult))
  }

  /** The token is read first, refreshed if stale, since the cleanup is what takes it away: an
   *  expired one would skip the revocation and leave every other device signed in. */
  async function leave(options: SignOutOptions = {}): Promise<Leaving> {
    let accessToken: string | null = null
    try {
      accessToken = await kernel.accessToken()
    } finally {
      await kernel.forgetActive()
      if (options.navigate !== false) navigate(kernel.config.postLogoutRedirectUri)
    }
    return { revoked: accessToken === null ? Promise.resolve() : globalSignOut(accessToken) }
  }

  return {
    async signIn(credentials) {
      const response = await initiateUserPasswordAuth(clientId, credentials)
      if (response.AuthenticationResult === undefined) return outcomeFrom(response)
      await establish(response)
      return { kind: 'signed-in' }
    },

    async completeNewPassword(challenge, newPassword) {
      await establish(await respondToNewPasswordRequired(clientId, challenge, newPassword))
    },

    async completeMfa(challenge, code) {
      await establish(await respondToSoftwareTokenMfa(clientId, challenge, code))
    },

    async changePassword(current, next) {
      const accessToken = await adminScopedAccessToken(kernel, 'change the password of')
      await changePasswordCall(accessToken, { current, next })
    },

    async requestReset(email) {
      const details = (await forgotPassword(clientId, email)).CodeDeliveryDetails
      return {
        medium: details?.DeliveryMedium ?? 'EMAIL',
        destination: details?.Destination ?? '',
      }
    },

    async confirmReset(input) {
      await confirmForgotPassword(clientId, input)
    },

    async signOut(options = {}) {
      const { revoked } = await leave(options)
      await revoked
    },

    leave,
  }
}
